import {Component, DestroyRef, Input, OnInit, inject, signal, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {VwuiAngularModule, VwuiModalService} from '@bvgo-ict/vwui-angular';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

import {ToastService} from '../../services';
import {ProjectNoteService} from '../../services/project-note/project-note.service';
import {ProjectDetails} from '../../models';
import {ProjectNote} from '../../models/project/project-note';
import {AddProjectNoteModalComponent} from '../add-project-note-modal/add-project-note-modal.component';

/**
 * "Notities" card on the project details page: shows the notes saved on
 * the project (manual ones and automatic ones, e.g. version creation).
 */
@Component({
    selector: 'app-project-notes-card',
    templateUrl: './project-notes-card.component.html',
    styleUrls: ['./project-notes-card.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, TranslatePipe, VwuiAngularModule]
})
export class ProjectNotesCardComponent implements OnInit {
    private readonly projectNoteService = inject(ProjectNoteService);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly modalService = inject(VwuiModalService);

    @Input({required: true}) public projectDetails: ProjectDetails;

    public readonly notes = signal<ProjectNote[]>([]);
    public readonly isLoading = signal(true);

    ngOnInit(): void {
        this.isLoading.set(true);
        this.loadNotes();

        this.projectNoteService.notesChanged$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((notes) => {
                this.notes.set(notes);
                this.isLoading.set(false);
            });
    }

    public trackNote(index: number): number {
        return index;
    }

    public getUser(note: ProjectNote): string {
        if (note.user?.name) {
            return note.user.name;
        }
        return note.externalUser ?? '';
    }

    public openAddNoteModal(): void {
        this.modalService.open(AddProjectNoteModalComponent, {
            closeOnBackdropClick: false,
            data: {projectId: this.projectDetails.id}
        });
    }

    private loadNotes(): void {
        this.projectNoteService.list(this.projectDetails.id).subscribe({
            next: (notes) => {
                this.notes.set(notes);
                this.isLoading.set(false);
            },
            error: (error) => {
                console.error(error);
                this.toastService.openError(error.status ?? 0, this.translateService.instant('ERRORS.notes_not_retrieved'));
                this.notes.set([]);
                this.isLoading.set(false);
            }
        });
    }
}
